// The BLOCKER (arc 101 slice 11 Part B) — a durable, ANSWERABLE object for the
// one thing the platform is allowed to stop for: a prerequisite only a human can
// supply.
//
// WHAT WAS BROKEN. A scout that reads a repo and finds a missing credential ends
// its run (`ScoutBlockedProposal`, #2024) — correct containment: it authors no
// plan, so no doomed coding runs are minted. But the containment was the whole
// mechanism. The answer arrives hours later and there is nothing to answer TO:
// the run is over, the tree is cold, the refusal existed only as prose in a run
// event, and somebody has to re-dispatch the work by hand (#2141).
//
// So: the scout STILL returns, unchanged — it writes one of these first. The row
// is what the answer lands on, and answering it mints a RAW TASK (slice 01)
// carrying both the original ask and the answer, so the resumption goes through
// triage like every other ask instead of through a second dispatch door.
//
// WHY THE RESUMPTION IS A RAW TASK AND NOT A `tasks` ROW. `docs/idea/lifecycle.md`
// § "The spine": a raw task is *what should be true*, an executable task is *what
// a box does next*, and only triage crosses between them. An answered blocker is
// squarely the first — nobody has yet decided which repo, which box, or whether
// the answer even makes the work possible. Minting executable work here would be
// the sixteenth inlet that skips the gate, in the same arc that built the gate.
//
// THE WARM BOX IS A HINT, NOT A FIELD ON THE RAW TASK. `raw_tasks` deliberately
// carries no `preferred_runner_id` — that is triage's output, and a raw task
// carrying it has already decided what triage exists to decide. The box that
// raised the blocker is recorded HERE, on `blockers.runner_id`, and the minted
// raw task points back through `origin_ref: {kind:'blocker', id}`. Triage reads
// it and PREFERS that box; it must never FILTER on it, or a drained or updated
// box starves the work it was supposed to accelerate.

import { z } from 'zod';
import {
  AccountIdSchema,
  type BlockerId,
  BlockerIdSchema,
  RawTaskIdSchema,
  TaskIdSchema,
} from '../value-objects/ids';
import { RepoFullNameSchema } from '../value-objects/task-envelope';
import { type BlockerGuidance, BlockerGuidanceSchema } from './blocker/guidance';
import { BlockerNeedsSchema, BlockerNeedsWriteSchema } from './blocker/needs-form';
import {
  BLOCKER_ANSWER_MAX,
  type BlockerOptions,
  BlockerOptionsSchema,
  BlockerOptionsWriteSchema,
} from './blocker/options';

// Three things a blocker is, each in its own module under `./blocker/` and re-exported
// here so `@developerz/domain` keeps ONE import path for all of them. Sorted, because
// biome's assist sorts exports and a hand order would be re-sorted under it.
//
// WHICH ARM an answer takes — whether it leaves an ask a judge reads, or resumes work
// that already exists (#6535): the wire projection, the answer door and the dashboard
// all ask it.
export * from './blocker/answer-arm';
// WHAT TO DO about a blocker, in plain language (#6679): numbered steps written for THIS
// row, for the asks whose answer is work rather than a judgement with options.
export * from './blocker/guidance';
// `needs` — the form a `missing_credential` blocker asks a human to fill (v2, epic #3756).
export * from './blocker/needs-form';
// The choices a `decision_needed` ask offers (#6387), and the answer members every door
// shares.
export * from './blocker/options';

/**
 * WHAT KIND of human input is owed. Three, closed, and each one is a condition a
 * human clearing it genuinely changes what the next cycle observes — the slice's
 * test for a legitimate hold. A condition that is local and deterministic does
 * not belong here: clearing it re-enters the same state and blocks again having
 * run zero sessions, which is a bug wearing a status.
 *
 * Stored in `blockers.kind` as text + Zod-at-read (the `tasks.status` stance), so
 * this union is a ONE-WAY DOOR: a member added later is a migration, a member
 * removed later is unreadable rows.
 */
export const BlockerKind = {
  /** A key, token or secret the work needs and the platform does not hold. */
  MissingCredential: 'missing_credential',
  /** A service the run must reach and cannot — a private registry, a VPN-fenced host. */
  UnreachableService: 'unreachable_service',
  /** A judgement only the asker can make. Not a technical gap; a genuine fork. */
  DecisionNeeded: 'decision_needed',
} as const;
export const BlockerKindSchema = z.enum(BlockerKind);
export type BlockerKind = z.infer<typeof BlockerKindSchema>;

/**
 * HOW a blocker stopped being open. Present exactly when the row is closed;
 * NULL is the open state, and it is the only open state.
 *
 * `expired` is a resolution rather than a delete, and that is the point of the
 * column: an unanswered blocker that timed out is EVIDENCE — it says the
 * platform asked and nobody came — and deleting it would leave the next
 * identical ask looking like a first offence.
 */
export const BlockerResolution = {
  /** A human supplied {@link Blocker.answer}; a raw task was minted from it. */
  Answered: 'answered',
  /** Withdrawn — the ask went away (the work was cancelled, or another route cleared it). */
  Withdrawn: 'withdrawn',
  /** Nobody answered inside {@link BLOCKER_EXPIRY_MS}. The record stays. */
  Expired: 'expired',
} as const;
export const BlockerResolutionSchema = z.enum(BlockerResolution);
export type BlockerResolution = z.infer<typeof BlockerResolutionSchema>;

/** One line in front of a human — a subject, never a dump. */
export const BLOCKER_SUMMARY_MAX = 200;
/** The scout's own words about what is missing. Matches `BLOCKED_REASON_MAX_CHARS` at the emit seam. */
export const BLOCKER_DETAIL_MAX = 500;
/** `(account_id, dedupe_key)` is unique — this is the length of the key half (the `raw_tasks` bound). */
export const BLOCKER_DEDUPE_KEY_MAX = 200;
/**
 * The blocker list's page-size CEILING, cited by its four users so none drifts
 * from the others:
 *   - `GET /v1/blockers`'s `per_page` query schema (`apps/api/src/routes/blockers.ts`);
 *   - the MCP `blocker list` action's `per_page` (`packages/mcp/src/army/blocker.ts`);
 *   - the store's clamp, `MAX_BLOCKER_LIST_LIMIT` (`packages/db/src/queries/blockers.ts`);
 *   - the dashboard's read (`apps/dashboard/src/lib/blockers.ts`), which asks for
 *     exactly this many and labels a full page truncatable.
 */
export const BLOCKER_LIST_MAX_PER_PAGE = 200;

/**
 * How long an unanswered blocker stays open.
 *
 * FOURTEEN DAYS, and the derivation is the same one the slice's evidence
 * supplies: the failure being fixed is a task pending ELEVEN days behind a
 * silent condition. An expiry shorter than the observed silence would close the
 * ask before the report that surfaces it has been read; one much longer keeps a
 * dead ask on the board indefinitely. Expiry CLOSES the record, it never deletes
 * it, so the cost of being wrong in either direction is legibility, not data.
 */
export const BLOCKER_EXPIRY_MS = 14 * 24 * 60 * 60_000;

/**
 * `origin_ref.kind` on the raw task an answer mints — the join back to the row
 * that asked. Named here rather than spelled at the write site, because triage
 * reads it to recover {@link Blocker.runnerId} as the warm-box affinity hint.
 */
export const BLOCKER_ORIGIN_KIND = 'blocker';

/**
 * The raw task's dedupe key for an answered blocker.
 *
 * Keyed on the BLOCKER, not on the answer or the moment: `raw_tasks` is unique
 * on `(account_id, dedupe_key)` and `createRawTask` answers `{kind:'existing'}`
 * on a conflict, so "answering twice mints exactly one raw task" is a property
 * of this key rather than a guard somebody has to remember. It is the second
 * half of the belt-and-braces — the answer write itself CASes on `resolution IS
 * NULL`, so a second answer never gets as far as minting.
 */
export function blockerRawTaskDedupeKey(blockerId: string): string {
  return `${BLOCKER_ORIGIN_KIND}:${blockerId}`;
}

/**
 * The READ half of {@link BLOCKER_ORIGIN_KIND}: which blocker, if any, a raw
 * task's `origin_ref` points back at. Named beside the write so the two cannot
 * drift — a `kind` string spelled at two sites is how a recovery quietly stops
 * matching the thing it was meant to recover.
 *
 * `null` for every other origin (a scout plan, a janitor finding, an issue), and
 * ALSO for a `blocker`-kinded ref whose id is not a `blk_` id: `origin_ref` is
 * free-form `{kind, id}` jsonb that any inlet may write, so the id is re-parsed
 * here rather than trusted. Both nulls mean the same thing to the one caller —
 * no warm-box hint — which is why they are one answer and not a verdict: the
 * hint is a PREFERENCE, so its absence costs nothing but a cold checkout.
 */
export function blockerIdFromOriginRef(ref: { kind: string; id: string }): BlockerId | null {
  if (ref.kind !== BLOCKER_ORIGIN_KIND) return null;
  const parsed = BlockerIdSchema.safeParse(ref.id);
  return parsed.success ? parsed.data : null;
}

/**
 * Compose the ask for the raw task an answer mints. Carries BOTH halves — the
 * original ask and the answer — because triage reading only the answer has no
 * idea what question it answers, and triage reading only the ask does not know
 * it has been cleared.
 *
 * Sliced to `max`, which the caller supplies as `RAW_TASK_ASK_MAX`: the caps
 * above already make the composed value an order of magnitude smaller, so the
 * slice is a fail-safe against a drifted cap rather than a shaper of real input.
 */
export function blockerAnswerAsk(input: {
  kind: BlockerKind;
  summary: string;
  detail: string;
  answer: string;
  /** The choices the ask offered, when it offered any — so triage sees what was NOT picked too. */
  options: BlockerOptions | null;
  /**
   * The guidance the person was SHOWN (#6679). Composed in so an answer like "Done: I
   * followed the steps shown on this blocker" carries what "the steps" were: the judge
   * reads the steps, then decides whether the retry can succeed, instead of taking a bare
   * "done" on faith. Only the `steps` member says anything; `none` and null add no line.
   */
  guidance: BlockerGuidance | null;
  max: number;
}): string {
  const offered =
    input.options === null
      ? []
      : [`Choices offered: ${input.options.choices.map((c) => c.label).join(' / ')}`];
  // The steps go AFTER the answer: the composition is sliced at `max`, and a long step list
  // must cost its own tail, never the answer it explains.
  const shown =
    input.guidance?.kind === 'steps'
      ? [
          ``,
          `Steps the platform showed the person:`,
          ...input.guidance.steps.map((step, i) => `${i + 1}. ${step}`),
        ]
      : [];
  return [
    `A blocker on this work was answered. Resume it.`,
    ``,
    `Blocker (${input.kind}): ${input.summary}`,
    `What was missing: ${input.detail}`,
    ...offered,
    ``,
    `The answer: ${input.answer}`,
    ...shown,
  ]
    .join('\n')
    .slice(0, input.max);
}

/**
 * The lifecycle invariant, at the READ boundary. `resolution`, `answered_at`,
 * `answered_by_user_id`, `answer` and `raw_task_id` are five spellings of ONE
 * position, and exactly three combinations exist:
 *
 * | resolution | answered_at / by / answer / raw_task_id | means |
 * |---|---|---|
 * | null | all null | OPEN — a human is owed |
 * | `answered` | `answered_at` + `answer` SET | a human answered |
 * | `withdrawn` \| `expired` | all null | closed without an answer |
 *
 * `answered_by_user_id` and `raw_task_id` are SET by the write that answers and
 * are NOT required by the invariant, because both are `ON DELETE SET NULL`
 * foreign keys — an offboarded user, or an account cascade that reaches
 * `raw_tasks` first, nulls them from underneath. Requiring them would have made
 * a member offboard abort on a check violation, so the requirement lives at the
 * WRITE (`answerBlocker` sets all four in one statement; the MCP verb refuses a
 * caller with no user principal) rather than here.
 *
 * Anything else is a row that contradicts itself: a closed blocker with no
 * resolution, an `answered` one carrying no answer, an `expired` one carrying
 * somebody's words. Enforced HERE and by a `CHECK` on the table — deliberately
 * both, the `raw_tasks` posture: the constraint makes a bad row unwritable, this
 * makes one already written unreadable rather than quietly acted on.
 */
function lifecycleRefinement(
  row: {
    resolution: BlockerResolution | null;
    answeredAt: Date | null;
    answeredByUserId: string | null;
    answer: string | null;
    rawTaskId: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  const refuse = (message: string, path: string): void => {
    ctx.addIssue({ code: 'custom', message, path: [path] });
  };
  const answered = row.resolution === BlockerResolution.Answered;
  const answerFields = [
    ['answeredAt', row.answeredAt],
    ['answeredByUserId', row.answeredByUserId],
    ['answer', row.answer],
    ['rawTaskId', row.rawTaskId],
  ] as const;
  // What an ANSWERED row must still carry. Deliberately NOT the whole list:
  // `answeredByUserId` and `rawTaskId` are `ON DELETE SET NULL` foreign keys, so
  // the database itself nulls them when an offboarded user or a cascaded
  // `raw_tasks` row goes — and a read that refused those rows would make a member
  // offboard corrupt every blocker that person ever answered. Attribution is
  // allowed to be lost to a deletion; the ANSWER is not.
  const requiredWhenAnswered = new Set(['answeredAt', 'answer']);
  for (const [name, value] of answerFields) {
    if (answered && value === null && requiredWhenAnswered.has(name)) {
      refuse(`an answered blocker must carry its ${name}`, name);
    }
    if (!answered && value !== null) {
      const state = row.resolution === null ? 'an open' : `a ${row.resolution}`;
      refuse(`${state} blocker carries no ${name}`, name);
    }
  }
}

/** The stored columns, without the lifecycle invariant — see {@link BlockerSchema}. */
const BlockerFieldsSchema = z.object({
  /** `blk_<hex>` — the branded id IS the stored text PK (the `tsk_`/`rtk_` scheme). */
  id: BlockerIdSchema,
  accountId: AccountIdSchema,
  /** The repo the blocked work is about. Null for an ask that names no repo. */
  repoFullName: RepoFullNameSchema.nullable(),
  /** The run that raised it (`fleet.evt.<run_id>`), when a run did. */
  runId: z.string().min(1).max(200).nullable(),
  /**
   * The task the blocker holds, when it holds one. NULL for a scout blocker:
   * the scout authored no plan, so there is no executable row to point at — which
   * is exactly why the blocker has to be its own object rather than a column.
   */
  taskId: TaskIdSchema.nullable(),
  /**
   * The box that raised it — the WARM-TREE affinity hint the resumption prefers.
   * PREFERS, never filters: if the box was drained or updated the resumed work
   * pays a cold clone, which is acceptable; starving it is not.
   */
  runnerId: z.string().min(1).max(200).nullable(),
  kind: BlockerKindSchema,
  summary: z.string().min(1).max(BLOCKER_SUMMARY_MAX),
  detail: z.string().min(1).max(BLOCKER_DETAIL_MAX),
  /** Idempotency: unique per account, derived from the run + the ask, never from the delivery. */
  dedupeKey: z.string().min(1).max(BLOCKER_DEDUPE_KEY_MAX),
  /**
   * The machine-readable half of a `missing_credential` ask — see
   * {@link BlockerNeedsSchema}. NULL for every other kind, and for a
   * `missing_credential` raised by a caller that could only describe the gap in
   * prose. Nullable rather than defaulted: "nobody named the fields" and "the
   * fields are none" are different facts, and only the first is true of every
   * row written before this column existed.
   */
  needs: BlockerNeedsSchema.nullable(),
  /**
   * The fixed choices a `decision_needed` ask offers — see {@link BlockerOptionsSchema}.
   * NULL for every ask that is not a pick. `.default(null)`, unlike `needs`: an
   * absent value here has only one meaning (no choices were offered), so every
   * row written before the column existed reads as exactly that.
   */
  options: BlockerOptionsSchema.nullable().default(null),
  /**
   * WHAT TO DO about this blocker, in plain language (#6679). NULL means the generator
   * has not tried yet, which is the whole of its one-attempt bound: a pass that came back
   * with nothing stores `{kind:'none', reason}`. `.default(null)` as `options`, so a
   * projection or fixture that omits the field reads as "not tried".
   */
  guidance: BlockerGuidanceSchema.nullable().default(null),
  askedAt: z.date(),
  answeredAt: z.date().nullable(),
  answeredByUserId: z.string().nullable(),
  /** The agent that typed the answer on that person's token; null for the person themselves. */
  answeredByAgent: z.string().nullable().optional(),
  answer: z.string().min(1).max(BLOCKER_ANSWER_MAX).nullable(),
  /** The raw task an answer minted — the join to the resumed work. Set iff `answered`. */
  rawTaskId: RawTaskIdSchema.nullable(),
  /** NULL is the OPEN state, and the only one. */
  resolution: BlockerResolutionSchema.nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * A blocker as stored. Parsed on the way OUT of `@developerz/db`, so a drifted
 * row — or one that contradicts itself — fails loudly at the read rather than
 * three layers later.
 */
export const BlockerSchema = BlockerFieldsSchema.superRefine(lifecycleRefinement);
export type Blocker = z.infer<typeof BlockerSchema>;

/**
 * What a RAISER hands `createBlocker` — the row minus everything the store owns
 * (`id`, `askedAt`, the whole answer half, timestamps). A raiser that could set
 * `resolution` could file a blocker already answered, which is the one thing this
 * object exists to prevent.
 */
export const BlockerDraftSchema = BlockerFieldsSchema.pick({
  accountId: true,
  repoFullName: true,
  runId: true,
  taskId: true,
  runnerId: true,
  kind: true,
  summary: true,
  detail: true,
  dedupeKey: true,
})
  .extend({
    /**
     * OPTIONAL on the draft, `null` in the row: most raisers have nothing to put
     * here, and forcing every one of them to spell `needs: null` is how a field
     * gets filled in with a guess.
     */
    needs: BlockerNeedsWriteSchema.optional(),
    /** OPTIONAL for the `needs` reason: only a raiser asking a fixed choice has any. */
    options: BlockerOptionsWriteSchema.optional(),
  })
  .superRefine((draft, ctx) => {
    // `needs` is the SHAPE OF A CREDENTIAL ASK, and the fix link minted from it
    // writes into a repo's secret vault. Refuse it on a kind that is not asking
    // for a credential HERE, at the seam it enters, rather than discovering at
    // link-mint time that a `decision_needed` row carries a vault capability.
    // Deliberately not mirrored on {@link BlockerSchema}: narrowing a READ is
    // data loss, and a stored row that contradicts this can only have come from
    // a write that bypassed this seam — which is a thing to see, not to hide.
    if (draft.needs !== undefined && draft.kind !== BlockerKind.MissingCredential) {
      ctx.addIssue({
        code: 'custom',
        message: `needs is the form a human fills to supply access, so it belongs only on a ${BlockerKind.MissingCredential} blocker, not on ${draft.kind}`,
        path: ['needs'],
      });
    }
    // `options` are the choices of a DECISION. On a credential ask they would offer
    // a pick where the answer is a location; on an unreachable service, a pick
    // where the answer is a fix. Refused at this seam, not mirrored on the read —
    // the `needs` stance above, for its reason.
    if (draft.options !== undefined && draft.kind !== BlockerKind.DecisionNeeded) {
      ctx.addIssue({
        code: 'custom',
        message: `options are the choices of a decision, so they belong only on a ${BlockerKind.DecisionNeeded} blocker, not on ${draft.kind}`,
        path: ['options'],
      });
    }
  });
export type BlockerDraft = z.infer<typeof BlockerDraftSchema>;
